import { useScienceTutorSection } from './scienceTutorContext';
import { useState } from 'react';

import { PolymersDigestionPanel, PolymersFoodEnergyPanel } from './PolymersFoodDigestion';
import { ModelLineChart } from './PolymersFoodChart';
import {
  ACTIVITY_CASES,
  ACTIVITY_Q10,
  DENATURATION_SPAN_C,
  activityAtPH,
  activityAtTemperature,
  activityCurve,
  activityQuestion,
  ENZYMES,
  INHIBITION_MODEL,
  INHIBITION_QUESTION,
  inhibitionCurve,
  PEAK_ACTIVITY,
  PH_RANGE,
  TEMPERATURE_RANGE,
  type ActivityCase,
  type Enzyme,
} from './polymersBiomoleculesFood';
import { FOOD_QUESTIONS } from './polymersBiomoleculesFoodQuestions';
import { PolymersEnzymeExplorer } from './PolymersEnzymeExplorer';
import { ChoicePanel } from './PolymersShared';
import { CARD_CLASS, chipClass } from './polymersStyles';

type Variable = ActivityCase['variable'];

const VARIABLE_LABEL: Record<Variable, string> = {
  temperature: 'Temperature',
  ph: 'pH',
};

function ActivityExplorer({ enzyme, item }: { enzyme: Enzyme; item: ActivityCase }) {
  const question = activityQuestion(item)!;
  const [revealed, setRevealed] = useState(false);
  const isTemperature = item.variable === 'temperature';
  const range = isTemperature ? TEMPERATURE_RANGE : PH_RANGE;
  const [value, setValue] = useState(
    isTemperature ? enzyme.optimumTemperatureC : enzyme.optimumPH,
  );
  useScienceTutorSection('ActivityExplorer', { revealed, value });
  const activity = isTemperature ? activityAtTemperature(enzyme, value) : activityAtPH(enzyme, value);
  const unit = isTemperature ? ' °C' : '';
  const prefix = isTemperature ? '' : 'pH ';

  return (
    <div className="mt-4 grid gap-5 lg:grid-cols-2">
      <div className="min-w-0">
        <ChoicePanel question={question} onChecked={() => setRevealed(true)} />
      </div>
      <div className="min-w-0 rounded-[1.5rem] border border-brand-sky/20 bg-wash-sky p-4">
        {revealed ? (
          <>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
              Model revealed · relative activity
            </p>
            <ModelLineChart
              series={[{ id: 'activity', points: activityCurve(enzyme, item.variable), tone: 'coral' }]}
              xMin={range.min}
              xMax={range.max}
              yMax={PEAK_ACTIVITY}
              xLabel={isTemperature ? 'Temperature (°C)' : 'pH'}
              yLabel="Activity (%)"
              ariaLabel={`Model activity of ${enzyme.name} against ${VARIABLE_LABEL[item.variable]}: peak 100 % at ${prefix}${isTemperature ? enzyme.optimumTemperatureC : enzyme.optimumPH}${unit}.`}
              marker={activity === null ? null : { x: value, y: activity }}
            />
            <label
              className="mt-3 block text-sm font-extrabold text-ink"
              htmlFor={`explore-${item.id}`}
            >
              {VARIABLE_LABEL[item.variable]}{' '}
              <span className="text-brand-coral">
                {prefix}
                {value}
                {unit}
              </span>{' '}
              → activity{' '}
              <span className="text-brand-coral">
                {activity === null ? '—' : `${Math.round(activity)} %`}
              </span>
            </label>
            <input
              id={`explore-${item.id}`}
              aria-label={`${VARIABLE_LABEL[item.variable]} for ${enzyme.name}`}
              type="range"
              min={range.min}
              max={range.max}
              step={range.step}
              value={value}
              onChange={(event) => setValue(Number(event.target.value))}
              className="mt-2 w-full accent-brand-coral"
            />
            <p className="mt-2 text-xs font-bold leading-5 text-slate2">
              {isTemperature
                ? `Below the optimum the model multiplies activity by ${ACTIVITY_Q10} for every 10 °C rise. Above it, the enzyme denatures and activity falls to zero within ${DENATURATION_SPAN_C} °C. Shown at the optimum pH.`
                : 'Model bell curve around the optimum pH, shown at the optimum temperature. Far from the optimum the active site loses its shape.'}
            </p>
          </>
        ) : (
          <p className="text-sm font-bold leading-6 text-slate2">
            Check an answer first. The model activity curve and a slider to explore it appear
            after your prediction.
          </p>
        )}
      </div>
    </div>
  );
}

function ActivityPanel() {
  const [enzymeId, setEnzymeId] = useState(ENZYMES[0].id);
  const [variable, setVariable] = useState<Variable>('ph');
  useScienceTutorSection('ActivityPanel', { enzymeId, variable });
  const enzyme = ENZYMES.find((entry) => entry.id === enzymeId) ?? ENZYMES[0];
  const item =
    ACTIVITY_CASES.find((entry) => entry.enzymeId === enzyme.id && entry.variable === variable) ??
    ACTIVITY_CASES[0];

  return (
    <section className={CARD_CLASS} aria-labelledby="enzyme-activity-heading">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
        1 · Predict, then see the curve
      </p>
      <h3 id="enzyme-activity-heading" className="mt-1 text-xl font-extrabold text-ink">
        Enzyme activity
      </h3>
      <div
        className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-5"
        role="group"
        aria-label="Choose an enzyme"
      >
        {ENZYMES.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-pressed={entry.id === enzyme.id}
            onClick={() => setEnzymeId(entry.id)}
            className={chipClass(entry.id === enzyme.id)}
          >
            {entry.name}
          </button>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Choose a variable">
        {(['ph', 'temperature'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={option === variable}
            onClick={() => setVariable(option)}
            className={chipClass(option === variable)}
          >
            {VARIABLE_LABEL[option]}
          </button>
        ))}
      </div>
      <p className="mt-3 text-sm font-medium leading-6 text-slate2">
        {enzyme.name} breaks down {enzyme.substrate} into {enzyme.products}.
      </p>
      <ActivityExplorer key={item.id} enzyme={enzyme} item={item} />
    </section>
  );
}

function InhibitionPanel() {
  const [revealed, setRevealed] = useState(false);
  useScienceTutorSection('InhibitionPanel', { revealed });
  return (
    <section className={CARD_CLASS} aria-labelledby="inhibition-heading">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
        2 · Competitive inhibition
      </p>
      <h3 id="inhibition-heading" className="mt-1 text-xl font-extrabold text-ink">
        Two molecules, one active site
      </h3>
      <div className="mt-4 grid gap-5 lg:grid-cols-2">
        <div className="min-w-0">
          <ChoicePanel question={INHIBITION_QUESTION} onChecked={() => setRevealed(true)} />
        </div>
        <div className="min-w-0 rounded-[1.5rem] border border-brand-sky/20 bg-wash-sky p-4">
          {revealed ? (
            <>
              <ModelLineChart
                series={[
                  { id: 'no-inhibitor', points: inhibitionCurve(false), tone: 'sky' },
                  { id: 'inhibitor', points: inhibitionCurve(true), tone: 'coral', dashed: true },
                ]}
                xMax={INHIBITION_MODEL.substrateMax}
                yMax={INHIBITION_MODEL.maxRate}
                xLabel="Substrate concentration (model units)"
                yLabel="Rate (%)"
                ariaLabel="Model rate against substrate concentration. Without inhibitor the solid curve rises quickly; with a competitive inhibitor the dashed curve rises more slowly but approaches the same maximum."
              />
              <ul className="mt-3 space-y-1 text-sm font-bold text-ink-soft">
                <li>Solid line: no inhibitor</li>
                <li>Dashed line: fixed amount of competitive inhibitor</li>
              </ul>
              <p className="mt-2 text-xs font-bold leading-5 text-slate2">
                Qualitative model in arbitrary units. Both curves head towards the same maximum
                rate; the inhibitor only means more substrate is needed to get close to it.
              </p>
            </>
          ) : (
            <p className="text-sm font-bold leading-6 text-slate2">
              Check an answer first. The rate curves with and without the inhibitor appear after
              your prediction.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function FoodQuestionBank() {
  const [index, setIndex] = useState(0);
  useScienceTutorSection('FoodQuestionBank', { index });
  const question = FOOD_QUESTIONS[index];
  return (
    <section className={CARD_CLASS} aria-labelledby="food-questions-heading">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3
          id="food-questions-heading"
          className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral"
        >
          5 · Check your understanding
        </h3>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Food and enzyme question">
          {FOOD_QUESTIONS.map((item, itemIndex) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={itemIndex === index}
              aria-label={`Food question ${itemIndex + 1}`}
              onClick={() => setIndex(itemIndex)}
              className={`h-9 min-w-9 rounded-full border px-3 text-sm font-extrabold ${
                itemIndex === index
                  ? 'border-ink bg-ink text-white'
                  : 'border-hairline bg-surface text-ink'
              }`}
            >
              {itemIndex + 1}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-3">
        <ChoicePanel key={question.id} question={question} />
      </div>
    </section>
  );
}

export function PolymersFoodEnzymesMode() {
  return (
    <div className="grid gap-6">
      <ActivityPanel />
      <PolymersEnzymeExplorer />
      <InhibitionPanel />
      <PolymersDigestionPanel />
      <PolymersFoodEnergyPanel />
      <FoodQuestionBank />
    </div>
  );
}
